import React, { useState } from 'react';
import {
  ExternalLink, MessageSquare, TrendingUp, BarChart3, Shield,
  Target, Zap, ChevronRight, Maximize2, Minimize2, X, Brain,
  Activity, DollarSign, BookOpen, Lightbulb, ArrowUpRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const ASKAPE_URL = 'https://app.askape.com';

const QUICK_ACTIONS = [
  {
    id: 'analyze',
    label: 'AI Stock Analysis',
    description: 'Get comprehensive AI grades: technical, fundamental, sentiment & risk',
    icon: Brain,
    color: 'text-indigo-400',
    bg: 'bg-indigo-500/10 hover:bg-indigo-500/20',
  },
  {
    id: 'options',
    label: 'Options Trade Setup',
    description: 'Find options plays with strike prices, expiry dates & entry/exit plans',
    icon: Target,
    color: 'text-emerald-400',
    bg: 'bg-emerald-500/10 hover:bg-emerald-500/20',
  },
  {
    id: 'support',
    label: 'Support & Resistance',
    description: 'AI-identified key price levels, volume zones & breakout points',
    icon: Activity,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10 hover:bg-amber-500/20',
  },
  {
    id: 'daytrade',
    label: 'Day Trade Setups',
    description: 'Real-time day trade ideas with entries, stops & targets',
    icon: Zap,
    color: 'text-red-400',
    bg: 'bg-red-500/10 hover:bg-red-500/20',
  },
  {
    id: 'swing',
    label: 'Swing Trade Ideas',
    description: 'Multi-day setups based on technical patterns & momentum',
    icon: TrendingUp,
    color: 'text-cyan-400',
    bg: 'bg-cyan-500/10 hover:bg-cyan-500/20',
  },
  {
    id: 'portfolio',
    label: 'Portfolio Health Check',
    description: 'Analyze your holdings, risk exposure & diversification',
    icon: Shield,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10 hover:bg-purple-500/20',
  },
];

const LEARNING_RESOURCES = [
  { label: 'What is AskApe?', url: 'https://learn.askape.com/readme/what-is-askape' },
  { label: 'Quick Start Guide', url: 'https://learn.askape.com/readme/quick-start-web' },
  { label: 'Day Trading Routine', url: 'https://learn.askape.com/use-cases/advanced/premarket-routine' },
  { label: 'First Options Trade', url: 'https://learn.askape.com/use-cases/Trader/first-options-trade' },
  { label: 'Snapshot Grades', url: 'https://learn.askape.com/Features/Ticker/snapshot-grades' },
  { label: 'AI Chat Tips', url: 'https://learn.askape.com/Features/Chat/basics' },
];

// Inline AskApe panel that can be embedded in any page
export const AskApeInlinePanel = ({ symbol, className = '' }) => {
  const openAskApe = (action) => {
    let url = ASKAPE_URL;
    if (symbol) {
      url = `${ASKAPE_URL}`;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Card className={`border-slate-700 bg-gradient-to-br from-[#1a1f2e] to-[#0f1219] ${className}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2 text-white">
            <div className="p-1.5 bg-green-500/20 rounded-md">
              <img src="https://askape.com/favicon.ico" alt="AskApe" className="h-4 w-4" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
            AskApe Expert
            {symbol && <Badge variant="outline" className="text-xs border-slate-600 text-slate-300 ml-2">{symbol}</Badge>}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-green-400 hover:text-green-300 hover:bg-green-500/10 text-xs"
            onClick={() => openAskApe()}
            data-testid="open-askape-btn"
          >
            Open AskApe <ExternalLink className="h-3 w-3 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid grid-cols-2 gap-2">
          {QUICK_ACTIONS.slice(0, 4).map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => openAskApe(action.id)}
                className={`flex items-center gap-2 p-2.5 rounded-lg border border-slate-700/50 transition-all text-left ${action.bg}`}
                data-testid={`askape-${action.id}`}
              >
                <Icon className={`h-4 w-4 shrink-0 ${action.color}`} />
                <span className="text-xs text-slate-300 font-medium">{action.label}</span>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

// Full AskApe Expert Resources page/tab
const AskApeExpert = () => {
  const [showEmbed, setShowEmbed] = useState(false);

  return (
    <div className="space-y-6" data-testid="askape-expert">
      {/* Header */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-[#0a1628] via-[#0f1f3a] to-[#0a1628] text-white overflow-hidden relative">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(16,185,129,0.08),transparent_60%)]" />
        <CardHeader className="pb-4 relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="text-2xl flex items-center gap-3">
                <div className="p-2.5 bg-green-500/20 rounded-xl border border-green-500/30">
                  <MessageSquare className="h-6 w-6 text-green-400" />
                </div>
                AskApe Expert
              </CardTitle>
              <p className="text-slate-400 mt-2 text-sm max-w-xl">
                AI-powered trading copilot for stock analysis, options setups, support/resistance levels, 
                and real-time trade ideas. Your go-to expert for trading questions.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setShowEmbed(!showEmbed)}
                variant="outline"
                className="border-green-500/40 text-green-400 hover:bg-green-500/20"
                data-testid="toggle-embed-btn"
              >
                {showEmbed ? <Minimize2 className="h-4 w-4 mr-2" /> : <Maximize2 className="h-4 w-4 mr-2" />}
                {showEmbed ? 'Hide Panel' : 'Open Panel'}
              </Button>
              <Button
                onClick={() => window.open(ASKAPE_URL, '_blank', 'noopener,noreferrer')}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="launch-askape-btn"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Launch AskApe
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Embedded AskApe Panel */}
      {showEmbed && (
        <Card className="border-slate-700 bg-slate-900 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 bg-slate-800 border-b border-slate-700">
            <span className="text-sm text-slate-300 flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-green-400" /> AskApe Quick Access
            </span>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white" onClick={() => setShowEmbed(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <CardContent className="py-8 text-center">
            <div className="max-w-md mx-auto space-y-4">
              <div className="p-4 bg-green-500/10 rounded-xl border border-green-500/20 inline-block">
                <MessageSquare className="h-10 w-10 text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-white">Open AskApe in a New Window</h3>
              <p className="text-sm text-slate-400">
                For the best experience, AskApe opens in its own dedicated window where you can chat with the AI, 
                analyze stocks, and get real-time trade setups.
              </p>
              <div className="flex items-center justify-center gap-3">
                <Button
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={() => window.open(ASKAPE_URL, '_blank', 'noopener,noreferrer')}
                  data-testid="panel-launch-btn"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Launch AskApe
                </Button>
                <Button
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  onClick={() => window.open('https://learn.askape.com/readme/quick-start-web', '_blank')}
                >
                  <BookOpen className="h-4 w-4 mr-2" />
                  Quick Start Guide
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Tip: Keep AskApe open alongside this platform for the best trading workflow
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions Grid */}
      <div>
        <h3 className="text-lg font-semibold text-slate-200 mb-3 flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-400" /> Quick Actions
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {QUICK_ACTIONS.map(action => {
            const Icon = action.icon;
            return (
              <button
                key={action.id}
                onClick={() => window.open(ASKAPE_URL, '_blank', 'noopener,noreferrer')}
                className={`group flex items-start gap-3 p-4 rounded-xl border border-slate-700/60 transition-all text-left hover:border-slate-600 ${action.bg}`}
                data-testid={`askape-action-${action.id}`}
              >
                <div className={`p-2 rounded-lg bg-slate-800/50 ${action.color} group-hover:scale-110 transition-transform`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white flex items-center gap-1">
                    {action.label}
                    <ChevronRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500" />
                  </p>
                  <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{action.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* What AskApe Can Do */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-slate-700 bg-slate-800/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <Lightbulb className="h-5 w-5 text-amber-400" /> What to Ask
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {[
                '"Analyze NVDA for a day trade setup"',
                '"What are the best options plays for TSLA this week?"',
                '"Give me support and resistance levels for SPY"',
                '"Should I buy calls or puts on AAPL right now?"',
                '"What stocks are gapping up premarket?"',
                '"Review my portfolio risk and suggest hedges"',
              ].map((q, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <MessageSquare className="h-3.5 w-3.5 text-green-400 shrink-0 mt-0.5" />
                  <span className="text-slate-300 italic">{q}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-700 bg-slate-800/40">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-white">
              <BookOpen className="h-5 w-5 text-blue-400" /> Learning Resources
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {LEARNING_RESOURCES.map((res, i) => (
                <a
                  key={i}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-slate-300 hover:text-green-400 transition-colors group"
                  data-testid={`resource-link-${i}`}
                >
                  <ArrowUpRight className="h-3.5 w-3.5 text-slate-500 group-hover:text-green-400 shrink-0" />
                  {res.label}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tool Comparison */}
      <Card className="border-slate-700 bg-slate-800/40">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2 text-white">
            <BarChart3 className="h-5 w-5 text-indigo-400" /> Expert Tools Comparison
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[480px]" data-testid="tools-comparison-table">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-2 text-slate-400 font-medium">Tool</th>
                  <th className="text-center py-2 text-slate-400 font-medium">Speed</th>
                  <th className="text-center py-2 text-slate-400 font-medium">Trading Focus</th>
                  <th className="text-center py-2 text-slate-400 font-medium">Options Data</th>
                  <th className="text-center py-2 text-slate-400 font-medium">UI</th>
                  <th className="text-center py-2 text-slate-400 font-medium">Rating</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'AskApe', speed: 'Fast', focus: 'Specialized', options: 'Excellent', ui: 'Trading-native', rating: 5, highlight: true },
                  { name: 'Gemini', speed: 'Fast', focus: 'General', options: 'Good', ui: 'Clean', rating: 4, highlight: false },
                  { name: 'Claude', speed: 'Medium', focus: 'General', options: 'Good', ui: 'Minimal', rating: 4, highlight: false },
                  { name: 'DeepSeek', speed: 'Fast', focus: 'General', options: 'Basic', ui: 'Developer-oriented', rating: 3, highlight: false },
                ].map((tool, i) => (
                  <tr key={i} className={`border-b border-slate-700/50 ${tool.highlight ? 'bg-green-500/5' : ''}`}>
                    <td className="py-2.5 font-medium text-white flex items-center gap-2">
                      {tool.highlight && <Badge className="bg-green-600 text-white text-xs px-1.5">Primary</Badge>}
                      {tool.name}
                    </td>
                    <td className="text-center text-slate-300">{tool.speed}</td>
                    <td className="text-center text-slate-300">{tool.focus}</td>
                    <td className="text-center text-slate-300">{tool.options}</td>
                    <td className="text-center text-slate-300">{tool.ui}</td>
                    <td className="text-center">
                      <div className="flex justify-center gap-0.5">
                        {[1,2,3,4,5].map(s => (
                          <div key={s} className={`w-2 h-2 rounded-full ${s <= tool.rating ? 'bg-green-500' : 'bg-slate-600'}`} />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Footer note */}
      <div className="text-center text-xs text-slate-500 pt-2">
        AskApe is an external service at <a href="https://askape.com" target="_blank" rel="noopener noreferrer" className="text-green-500 hover:underline">askape.com</a>. 
        Subscription required for full access ($25/mo).
      </div>
    </div>
  );
};

export default AskApeExpert;
